import { useMemo, useState } from 'react';
import {
  AlarmClock,
  CalendarClock,
  CheckCircle2,
  Database,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';

import { KpiCard, formatNumber } from '@/features/dashboard/widgets';
import { qualityApi } from '@/features/quality/api';
import { useReportDatasets } from '@/features/reporting/hooks';
import { ScheduleFormModal } from '@/features/scheduler/components/ScheduleFormModal';
import { loadSchedules, saveSchedules, uid } from '@/features/scheduler/storage';
import {
  computeNextRun,
  describeSchedule,
  formatRelative,
  FREQUENCY_LABELS,
  type ProfileSchedule,
  type RunStatus,
} from '@/features/scheduler/types';

function agoText(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function StatusPill({ status }: { status: RunStatus }): JSX.Element {
  const map: Record<RunStatus, { cls: string; label: string }> = {
    success: { cls: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300', label: 'Success' },
    failed: { cls: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300', label: 'Failed' },
    running: { cls: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', label: 'Running' },
    never: { cls: 'bg-ink-100 text-ink-500 dark:bg-white/10 dark:text-ink-300', label: 'Never run' },
  };
  const m = map[status];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

export function SchedulerPage(): JSX.Element {
  const [schedules, setSchedules] = useState<ProfileSchedule[]>(() => loadSchedules());
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProfileSchedule | null>(null);
  const datasetsQ = useReportDatasets();

  function persist(next: ProfileSchedule[]): void {
    setSchedules(next);
    saveSchedules(next);
  }

  function onSave(s: ProfileSchedule): void {
    if (s.id) {
      persist(schedules.map((x) => (x.id === s.id ? s : x)));
    } else {
      persist([{ ...s, id: uid(), createdAt: new Date().toISOString() }, ...schedules]);
    }
    setModalOpen(false);
    setEditing(null);
  }

  function toggleEnabled(id: string): void {
    persist(schedules.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  }

  function remove(id: string): void {
    if (window.confirm('Delete this schedule?')) persist(schedules.filter((s) => s.id !== id));
  }

  async function runNow(s: ProfileSchedule): Promise<void> {
    setSchedules((prev) => prev.map((x) => (x.id === s.id ? { ...x, lastStatus: 'running' } : x)));
    let status: RunStatus = 'success';
    try {
      if (s.datasetIds.length > 0) {
        await Promise.all(s.datasetIds.map((id) => qualityApi.profileDataset(id)));
      } else {
        await new Promise((r) => setTimeout(r, 1200)); // seeded examples have no live target
      }
    } catch {
      status = 'failed';
    }
    setSchedules((prev) => {
      const next = prev.map((x) =>
        x.id === s.id ? { ...x, lastStatus: status, lastRun: new Date().toISOString() } : x,
      );
      saveSchedules(next);
      return next;
    });
  }

  const stats = useMemo(() => {
    const active = schedules.filter((s) => s.enabled);
    const nexts = active.map((s) => computeNextRun(s)).filter((d): d is Date => d !== null).sort((a, b) => a.getTime() - b.getTime());
    const datasets = new Set(schedules.flatMap((s) => s.datasetNames));
    return {
      active: active.length,
      paused: schedules.length - active.length,
      next: nexts[0] ?? null,
      datasets: datasets.size,
    };
  }, [schedules]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow eyebrow-dot mb-2.5">
            <span>MODULE</span>
          </div>
          <h1 className="h-display">Profiling Scheduler</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
            Automate data-quality profiling across your estate — schedule jobs per dataset, choose what to measure,
            and control execution, retries and alerting. Run any job on demand.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          New schedule
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Active Schedules" value={formatNumber(stats.active)} sub="running automatically" icon={CalendarClock} accent="brand" />
        <KpiCard label="Paused" value={formatNumber(stats.paused)} sub="disabled, not running" icon={Pause} accent="amber" />
        <KpiCard label="Next Run" value={stats.next ? formatRelative(stats.next) : '—'} sub={stats.next ? stats.next.toLocaleString() : 'no active jobs'} icon={AlarmClock} accent="sky" />
        <KpiCard label="Datasets Covered" value={formatNumber(stats.datasets)} sub="across all schedules" icon={Database} accent="violet" />
      </div>

      {/* Table */}
      <section className="rounded-xl border border-ink-200/70 bg-white shadow-soft dark:border-white/10 dark:bg-ink-900">
        <header className="border-b border-ink-100 px-6 py-4 dark:border-white/5">
          <h2 className="font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">Schedules</h2>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{schedules.length} job(s) configured.</p>
        </header>
        <div className="overflow-x-auto p-2">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-400 dark:border-white/10 dark:text-ink-500">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Target</th>
                <th className="px-4 py-2 font-medium">Schedule</th>
                <th className="px-4 py-2 font-medium">Next run</th>
                <th className="px-4 py-2 font-medium">Last run</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-white/5">
              {schedules.map((s) => {
                const next = computeNextRun(s);
                return (
                  <tr key={s.id} className="align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink-900 dark:text-ink-50">{s.name}</div>
                      <div className="mt-0.5 max-w-xs truncate text-xs text-ink-500 dark:text-ink-400">{s.description}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-600 dark:text-ink-300">
                      {s.datasetNames.join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-600 dark:text-ink-300">
                      <span className="rounded bg-ink-100 px-1.5 py-0.5 font-medium dark:bg-white/10">{FREQUENCY_LABELS[s.frequency]}</span>
                      <div className="mt-1 text-ink-400 dark:text-ink-500">{describeSchedule(s)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="text-ink-700 dark:text-ink-200">{s.enabled ? formatRelative(next) : 'paused'}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-500 dark:text-ink-400">{agoText(s.lastRun)}</td>
                    <td className="px-4 py-3">
                      {s.enabled ? <StatusPill status={s.lastStatus} /> : <span className="inline-flex items-center rounded-full bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-500 dark:bg-white/10 dark:text-ink-300">Paused</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 text-ink-400 dark:text-ink-500">
                        <button title="Run now" onClick={() => runNow(s)} disabled={s.lastStatus === 'running'} className="rounded-md p-1.5 hover:bg-brand-50 hover:text-brand-600 disabled:opacity-40 dark:hover:bg-white/10">
                          <Play className="h-4 w-4" />
                        </button>
                        <button title={s.enabled ? 'Pause' : 'Resume'} onClick={() => toggleEnabled(s.id)} className="rounded-md p-1.5 hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-white/10 dark:hover:text-ink-200">
                          {s.enabled ? <Pause className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </button>
                        <button title="Edit" onClick={() => { setEditing(s); setModalOpen(true); }} className="rounded-md p-1.5 hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-white/10 dark:hover:text-ink-200">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button title="Delete" onClick={() => remove(s.id)} className="rounded-md p-1.5 hover:bg-red-50 hover:text-accent-crimson dark:hover:bg-white/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {schedules.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-400 dark:text-ink-500">
                    <XCircle className="mx-auto mb-2 h-6 w-6 opacity-40" />
                    No schedules yet. Click <strong>New schedule</strong> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-ink-400 dark:text-ink-500">
        Note: the scheduling engine is a work in progress — schedules are saved and “Run now” profiles live, but
        automatic timed execution is not yet wired up.
      </p>

      <ScheduleFormModal
        open={modalOpen}
        initial={editing}
        datasets={datasetsQ.data ?? []}
        onSave={onSave}
        onClose={() => { setModalOpen(false); setEditing(null); }}
      />
    </div>
  );
}
